import { z } from 'zod';

/**
 * Campos que un tipo de activo puede pedir al registrarlo (p. ej. MAC para un equipo,
 * licencias para un software). Cada tipo elige cuáles usa y cuáles son obligatorios.
 * El valor de cada campo se guarda por activo en `activo_atributos`.
 */
export const CAMPOS_ACTIVO = {
  mac: { etiqueta: 'Dirección MAC', tipo: 'texto' },
  ip: { etiqueta: 'Dirección IP', tipo: 'texto' },
  dhcp: { etiqueta: 'Usa DHCP', tipo: 'si_no' },
  custodia_tecnica: { etiqueta: 'Custodio técnico', tipo: 'texto' },
  usuario_asignado: { etiqueta: 'Usuario asignado', tipo: 'texto' },
  clasificacion_informacion: {
    etiqueta: 'Clasificación máxima de la información',
    tipo: 'lista',
    opciones: ['Pública', 'Interna', 'Confidencial', 'Restringida'],
  },
  cifrado_disco: { etiqueta: 'Cifrado de disco', tipo: 'si_no' },
  agente_edr: { etiqueta: 'Agente EDR / antimalware', tipo: 'si_no' },
  proveedor: { etiqueta: 'Proveedor', tipo: 'texto' },
  fecha_instalacion: { etiqueta: 'Fecha de instalación', tipo: 'fecha' },
  numero_licencias: { etiqueta: 'N.º de licencias', tipo: 'numero' },
  grupo: { etiqueta: 'Grupo', tipo: 'texto' },
  ultima_revision: { etiqueta: 'Última revisión', tipo: 'fecha' },
  proxima_revision: { etiqueta: 'Próxima revisión', tipo: 'fecha' },

  // ── Catálogo de software y sistemas (CIS 2.1, 2.2) ──
  tipo_software: {
    etiqueta: 'Tipo de software',
    tipo: 'lista',
    opciones: [
      'Instalado en equipos',
      'SaaS',
      'Aplicación web propia',
      'Desarrollo interno',
      'Infraestructura y nube',
      'Sistema operativo',
      'Móvil',
      'Extensión / complemento',
      'Librería / componente',
    ],
  },
  version: { etiqueta: 'Versión (o mínima soportada)', tipo: 'texto' },
  url_acceso: { etiqueta: 'URL / ruta de acceso', tipo: 'texto' },
  repositorio_codigo: { etiqueta: 'Repositorio de código', tipo: 'texto' },
  propietario_area: { etiqueta: 'Propietario (área solicitante)', tipo: 'texto' },
  usuarios_areas: { etiqueta: 'Usuarios / áreas', tipo: 'texto' },
  datos_personales: {
    etiqueta: '¿Trata datos personales o de pacientes?',
    tipo: 'lista',
    opciones: ['Sí', 'No', 'Confirmar'],
  },
  mfa: {
    etiqueta: 'MFA',
    tipo: 'lista',
    opciones: ['Sí', 'Parcial', 'No', 'No admite', 'No aplica', 'Confirmar'],
  },
  alojamiento: {
    etiqueta: 'Alojamiento',
    tipo: 'lista',
    opciones: ['Equipo local', 'AWS (DEPILAB)', 'Proveedor (nube)', 'Proveedor (GTD)', 'Servidor local', 'Otro'],
  },
  responsable_parches: { etiqueta: 'Responsable de parches y vulnerabilidades', tipo: 'texto' },
  integraciones: { etiqueta: 'Integraciones', tipo: 'texto' },
  tipo_licencia: { etiqueta: 'Tipo de licencia', tipo: 'texto' },
  mecanismo_despliegue: { etiqueta: 'Mecanismo de despliegue', tipo: 'texto' },
  fin_soporte: { etiqueta: 'Fin de soporte', tipo: 'fecha' },
  autorizado: {
    etiqueta: '¿Autorizado?',
    tipo: 'lista',
    opciones: ['Sí', 'No', 'Excepción', 'En evaluación'],
  },
  referencia_excepcion: { etiqueta: 'Referencia de excepción', tipo: 'texto' },
  fecha_retiro: { etiqueta: 'Fecha de retiro', tipo: 'fecha' },
  observaciones: { etiqueta: 'Observaciones', tipo: 'texto' },
} as const;

export type CampoActivo = keyof typeof CAMPOS_ACTIVO;
export type TipoCampoActivo = (typeof CAMPOS_ACTIVO)[CampoActivo]['tipo'];

export const CODIGOS_CAMPO_ACTIVO = Object.keys(CAMPOS_ACTIVO) as [CampoActivo, ...CampoActivo[]];
export const campoActivoSchema = z.enum(CODIGOS_CAMPO_ACTIVO);

/** `AAAA-MM-DD` que existe en el calendario (rechaza p. ej. 2026-02-30, que Date.parse aceptaría). */
function esFechaReal(valor: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (!m) return false;
  const [anio, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const fecha = new Date(Date.UTC(anio, mes - 1, dia));
  return fecha.getUTCFullYear() === anio && fecha.getUTCMonth() === mes - 1 && fecha.getUTCDate() === dia;
}

/** Valida y normaliza el valor de un campo. Devuelve el texto a guardar o un mensaje de error. */
export function valorCampoActivo(campo: CampoActivo, bruto: string): { valor: string } | { error: string } {
  const definicion = CAMPOS_ACTIVO[campo];
  const valor = bruto.trim();
  switch (definicion.tipo) {
    case 'texto':
      return valor.length > 300 ? { error: `${definicion.etiqueta}: máximo 300 caracteres.` } : { valor };
    case 'si_no':
      if (valor !== 'si' && valor !== 'no') return { error: `${definicion.etiqueta}: elija Sí o No.` };
      return { valor };
    case 'fecha':
      if (!esFechaReal(valor)) return { error: `${definicion.etiqueta}: fecha no válida.` };
      return { valor };
    case 'numero':
      if (!/^\d{1,9}$/.test(valor)) return { error: `${definicion.etiqueta}: ingrese un número entero.` };
      return { valor };
    case 'lista':
      return (definicion.opciones as readonly string[]).includes(valor)
        ? { valor }
        : { error: `${definicion.etiqueta}: opción no válida.` };
  }
}
